// SEO Configuration for different pages
export const seoConfig = {
  // Home/Dashboard page
  dashboard: {
    title: "Dashboard - Mirats Quanto DIY | Research Study Management",
    description: "Manage your research studies with Mirats Quanto DIY dashboard. Track surveys, analyze data, and monitor your research projects in one place.",
    keywords: "research dashboard, survey management, data analytics, research tracking, Mirats Quanto DIY",
    ogTitle: "Dashboard - Mirats Quanto DIY Research Management",
    ogDescription: "Comprehensive dashboard for managing your research studies and survey projects with Mirats Quanto DIY.",
    pageType: "website"
  },

  // Surveys page
  surveys: {
    title: "Surveys - Mirats Quanto DIY | Create & Manage Research Surveys",
    description: "Create, manage, and track your research surveys with Mirats Quanto DIY. Integrate with Qualtrics, Survey Monkey, and other platforms seamlessly.",
    keywords: "survey creation, survey management, Qualtrics integration, Survey Monkey, research surveys, survey platform",
    ogTitle: "Survey Management - Mirats Quanto DIY",
    ogDescription: "Create and manage research surveys with seamless integration to popular survey platforms.",
    pageType: "website"
  },

  // Survey Groups page
  surveyGroups: {
    title: "Survey Groups - Mirats Quanto DIY | Organize Research Studies",
    description: "Organize your research studies into survey groups for better management and tracking with Mirats Quanto DIY platform.",
    keywords: "survey groups, research organization, study management, survey categorization, research workflow",
    ogTitle: "Survey Groups - Organize Your Research Studies",
    ogDescription: "Efficiently organize and manage your research studies with survey groups.",
    pageType: "website"
  },

  // Feasibility page
  feasibility: {
    title: "Feasibility Analysis - Mirats Quanto DIY | Research Study Planning",
    description: "Analyze the feasibility of your research studies with Mirats Quanto DIY. Get insights on target audience availability and study viability.",
    keywords: "feasibility analysis, research planning, study viability, target audience analysis, research feasibility",
    ogTitle: "Feasibility Analysis - Plan Your Research Studies",
    ogDescription: "Analyze and plan your research studies with comprehensive feasibility tools.",
    pageType: "website"
  },

  // Qualification Library page
  qualificationLibrary: {
    title: "Qualification Library - Mirats Quanto DIY | Audience Targeting",
    description: "Access our comprehensive qualification library for precise audience targeting in your research studies with Mirats Quanto DIY.",
    keywords: "qualification library, audience targeting, demographic filters, research targeting, participant screening",
    ogTitle: "Qualification Library - Precise Audience Targeting",
    ogDescription: "Find the perfect audience for your research with our qualification library.",
    pageType: "website"
  },

  // Invoice Billing page
  invoiceBilling: {
    title: "Invoice & Billing - Mirats Quanto DIY | Research Study Payments",
    description: "Manage your research study payments and invoices with Mirats Quanto DIY. Track expenses and billing for all your research projects.",
    keywords: "invoice management, research billing, payment tracking, study expenses, billing dashboard",
    ogTitle: "Invoice & Billing - Manage Research Payments",
    ogDescription: "Streamlined billing and payment management for your research studies.",
    pageType: "website"
  },

  // Profile page
  profile: {
    title: "Profile - Mirats Quanto DIY | Account Management",
    description: "Manage your Mirats Quanto DIY account settings, notifications, and preferences for optimal research study experience.",
    keywords: "account management, profile settings, user preferences, account configuration, Mirats Quanto DIY profile",
    ogTitle: "Profile - Manage Your Account Settings",
    ogDescription: "Customize your Mirats Quanto DIY experience with profile management.",
    pageType: "website"
  },

  // Payment page
  payment: {
    title: "Payment - Mirats Quanto DIY | Secure Research Study Payments",
    description: "Secure payment processing for your research studies with Mirats Quanto DIY. Multiple payment options and secure transactions.",
    keywords: "secure payments, research study payments, payment processing, billing, transaction security",
    ogTitle: "Secure Payment Processing - Mirats Quanto DIY",
    ogDescription: "Secure and reliable payment processing for your research studies.",
    pageType: "website"
  },

  // Terms and Conditions
  termsAndConditions: {
    title: "Terms and Conditions - Mirats Quanto DIY | Legal Information",
    description: "Read the terms and conditions for using Mirats Quanto DIY platform for your research studies and data collection.",
    keywords: "terms and conditions, legal terms, user agreement, platform terms, Mirats Quanto DIY terms",
    ogTitle: "Terms and Conditions - Mirats Quanto DIY",
    ogDescription: "Legal terms and conditions for using Mirats Quanto DIY platform.",
    pageType: "website"
  },

  // Privacy Policy
  privacyPolicy: {
    title: "Privacy Policy - Mirats Quanto DIY | Data Protection & Privacy",
    description: "Learn about how Mirats Quanto DIY protects your data and privacy in research studies. GDPR compliant data handling.",
    keywords: "privacy policy, data protection, GDPR compliance, data privacy, research data security",
    ogTitle: "Privacy Policy - Data Protection & Privacy",
    ogDescription: "Comprehensive privacy policy and data protection information for Mirats Quanto DIY.",
    pageType: "website"
  },

  // Refund Policy
  refundPolicy: {
    title: "Refund Policy - Mirats Quanto DIY | Research Study Refunds",
    description: "Understand our refund policy for research studies and survey services with Mirats Quanto DIY platform.",
    keywords: "refund policy, research refunds, survey refunds, money back guarantee, refund terms",
    ogTitle: "Refund Policy - Research Study Refunds",
    ogDescription: "Clear refund policy for research studies and survey services.",
    pageType: "website"
  },

  // Survey Details page (dynamic)
  surveyDetails: (surveyId, surveyName) => ({
    title: `${surveyName || 'Survey'} Details - Mirats Quanto DIY | Research Study Management`,
    description: `Detailed view and management for your research survey: ${surveyName || 'Survey'}. Track responses, manage quotas, and analyze data with Mirats Quanto DIY.`,
    keywords: `survey details, ${surveyName || 'survey'} management, research study tracking, survey analytics, response tracking`,
    ogTitle: `${surveyName || 'Survey'} Details - Research Study Management`,
    ogDescription: `Comprehensive survey management and analytics for: ${surveyName || 'Survey'}`,
    pageType: "website"
  }),

  // Survey Taking pages
  surveyTaking: {
    title: "Take Survey - Mirats Quanto DIY | Research Study Participation",
    description: "Participate in research studies through Mirats Quanto DIY. Share your valuable insights and contribute to meaningful research.",
    keywords: "take survey, research participation, survey completion, study participation, research insights",
    ogTitle: "Take Survey - Participate in Research Studies",
    ogDescription: "Join research studies and share your valuable insights through our platform.",
    pageType: "website"
  },

  // GDPR Consent page
  gdprConsent: {
    title: "GDPR Consent - Mirats Quanto DIY | Data Protection Compliance",
    description: "GDPR compliant data consent for research studies. Learn how your data is protected and used in Mirats Quanto DIY research.",
    keywords: "GDPR consent, data protection, research consent, privacy compliance, data usage consent",
    ogTitle: "GDPR Consent - Data Protection Compliance",
    ogDescription: "GDPR compliant consent process for research study participation.",
    pageType: "website"
  },

  // Onboarding page
  onboarding: {
    title: "Getting Started - Mirats Quanto DIY | Research Platform Setup",
    description: "Get started with Mirats Quanto DIY platform. Learn how to create your first research study and access the perfect audience.",
    keywords: "getting started, platform setup, research platform tutorial, DIY research setup, study creation guide",
    ogTitle: "Getting Started - Mirats Quanto DIY Platform",
    ogDescription: "Complete setup guide for starting your research journey with Mirats Quanto DIY.",
    pageType: "website"
  },

  // Help Center
  helpCenter: {
    title: "Help Center - Mirats Quanto DIY | Guides & Support",
    description: "Find answers, how-to guides, and support for Mirats Quanto DIY. Learn how to create surveys, manage redirects, and get the most from the platform.",
    keywords: "help center, support, FAQ, Mirats Quanto DIY help, survey guides, redirect setup",
    ogTitle: "Help Center - Mirats Quanto DIY",
    ogDescription: "Guides, FAQs, and support for Mirats Quanto DIY research platform.",
    pageType: "website"
  },

  // API Documentation
  apiDocumentation: {
    title: "API Documentation - Mirats Quanto DIY | Developer Guide",
    description: "Mirats Quanto DIY API reference: create surveys, survey groups, and fetch sessions. Authenticate with API keys and integrate with your systems.",
    keywords: "API documentation, developer API, survey API, Mirats Quanto DIY API, REST API",
    ogTitle: "API Documentation - Mirats Quanto DIY",
    ogDescription: "Complete API reference for integrating with Mirats Quanto DIY.",
    pageType: "website"
  },

  // Redirects & Keys (profile subpage)
  redirectsAndKeys: {
    title: "Redirects & Secret Key - Mirats Quanto DIY | Postback Setup",
    description: "Configure redirect endpoints and manage your secret key for survey postbacks. Set up complete, terminate, and quota full URLs in your portal.",
    keywords: "redirects setup, secret key, postback URL, survey redirects, status codes, Mirats Quanto DIY",
    ogTitle: "Redirects & Secret Key Setup - Mirats Quanto DIY",
    ogDescription: "Configure redirect endpoints and secret key for survey integration.",
    pageType: "website"
  },

  // Edit Survey Group
  surveyGroupEdit: {
    title: "Edit Survey Group - Mirats Quanto DIY | Organize Studies",
    description: "Edit your survey group and manage associated surveys with Mirats Quanto DIY.",
    keywords: "edit survey group, survey group management, research organization",
    ogTitle: "Edit Survey Group - Mirats Quanto DIY",
    ogDescription: "Edit and manage your survey group.",
    pageType: "website"
  },

  // Invoice Detail
  invoiceDetail: {
    title: "Invoice Details - Mirats Quanto DIY | Billing",
    description: "View and manage invoice details for your research studies with Mirats Quanto DIY.",
    keywords: "invoice details, billing, research study invoice",
    ogTitle: "Invoice Details - Mirats Quanto DIY",
    ogDescription: "Detailed view of your research study invoice.",
    pageType: "website"
  },

  // E-Invoice (public)
  eInvoice: {
    title: "E-Invoice - Mirats Quanto DIY | View Invoice",
    description: "View your electronic invoice from Mirats Quanto DIY.",
    keywords: "e-invoice, electronic invoice, view invoice",
    ogTitle: "E-Invoice - Mirats Quanto DIY",
    ogDescription: "View your invoice online.",
    pageType: "website",
    robots: "noindex, nofollow"
  },

  // Reconciliation Verification
  reconciliationVerification: {
    title: "Reconciliation Verification - Mirats Quanto DIY",
    description: "Verify reconciliation status for your research study with Mirats Quanto DIY.",
    keywords: "reconciliation, verification, survey reconciliation",
    ogTitle: "Reconciliation Verification - Mirats Quanto DIY",
    ogDescription: "Verify your survey reconciliation status.",
    pageType: "website",
    robots: "noindex, nofollow"
  },

  // Checkout Success / Failure
  checkoutSuccess: {
    title: "Payment Successful - Mirats Quanto DIY",
    description: "Your payment was processed successfully.",
    keywords: "payment success, checkout",
    ogTitle: "Payment Successful - Mirats Quanto DIY",
    ogDescription: "Payment completed successfully.",
    pageType: "website",
    robots: "noindex, nofollow"
  },
  checkoutFailure: {
    title: "Transaction Failed - Mirats Quanto DIY",
    description: "Your transaction could not be completed. Please try again or contact support.",
    keywords: "transaction failed, payment error",
    ogTitle: "Transaction Failed - Mirats Quanto DIY",
    ogDescription: "Payment could not be completed.",
    pageType: "website",
    robots: "noindex, nofollow"
  },

  // Auth (noindex)
  login: {
    title: "Log In - Mirats Quanto DIY",
    description: "Log in to your Mirats Quanto DIY account to manage research studies and surveys.",
    keywords: "login, sign in, Mirats Quanto DIY",
    ogTitle: "Log In - Mirats Quanto DIY",
    ogDescription: "Sign in to your account.",
    pageType: "website",
    robots: "noindex, nofollow"
  },
  signUp: {
    title: "Sign Up - Mirats Quanto DIY | Create Account",
    description: "Create your Mirats Quanto DIY account to access the perfect audience for your research studies.",
    keywords: "sign up, register, create account, Mirats Quanto DIY",
    ogTitle: "Sign Up - Mirats Quanto DIY",
    ogDescription: "Create your account and get started.",
    pageType: "website",
    robots: "noindex, nofollow"
  },

  // Account disabled
  accountDisabled: {
    title: "Account Disabled - Mirats Quanto DIY",
    description: "This account has been disabled. Please contact support.",
    keywords: "account disabled",
    ogTitle: "Account Disabled - Mirats Quanto DIY",
    ogDescription: "Account access has been disabled.",
    pageType: "website",
    robots: "noindex, nofollow"
  },

  // Payment account (detail)
  paymentAccount: {
    title: "Payment Account - Mirats Quanto DIY | Billing Details",
    description: "Manage your payment account and billing details for Mirats Quanto DIY research studies.",
    keywords: "payment account, billing details, payment methods",
    ogTitle: "Payment Account - Mirats Quanto DIY",
    ogDescription: "Your payment and billing account details.",
    pageType: "website"
  },

  // Survey not live / error
  surveyNotLive: {
    title: "Survey Not Available - Mirats Quanto DIY",
    description: "This survey is not currently available.",
    keywords: "survey not available, survey error",
    ogTitle: "Survey Not Available - Mirats Quanto DIY",
    ogDescription: "The survey you are looking for is not available.",
    pageType: "website",
    robots: "noindex, nofollow"
  },

  // Ad blocker detected
  adBlockerDetected: {
    title: "Ad Blocker Detected - Mirats Quanto DIY",
    description: "Please disable your ad blocker to continue with this survey.",
    keywords: "ad blocker, survey access",
    ogTitle: "Ad Blocker Detected - Mirats Quanto DIY",
    ogDescription: "Disable ad blocker to continue.",
    pageType: "website",
    robots: "noindex, nofollow"
  }
};

// Default SEO configuration
export const defaultSEO = {
  title: "Mirats Quanto DIY - Perfect Audience for Research Studies",
  description: "Mirats Quanto DIY is an innovative platform designed for clients to effortlessly access the perfect audience for their research studies. Seamlessly integrate survey links from Qualtrics, Survey Monkey, and other platforms.",
  keywords: "Mirats Quanto DIY, survey research, audience targeting, Qualtrics integration, Survey Monkey, market research, data collection, research studies, survey platform, DIY research",
  ogTitle: "Mirats Quanto DIY - Perfect Audience for Research Studies",
  ogDescription: "Access the perfect audience for your research studies with Mirats Quanto DIY. Seamlessly integrate survey links from Qualtrics, Survey Monkey, and other platforms.",
  ogImage: "https://app.miratsquanto.com/og-image.jpg",
  ogUrl: "https://app.miratsquanto.com",
  canonicalUrl: "https://app.miratsquanto.com",
  pageType: "website"
};

// Structured data for the main website
export const websiteStructuredData = {
  "@context": "https://schema.org",
  "@type": "WebApplication",
  "name": "Mirats Quanto DIY",
  "description": "Innovative platform for accessing the perfect audience for research studies with seamless survey integration",
  "url": "https://app.miratsquanto.com",
  "applicationCategory": "BusinessApplication",
  "operatingSystem": "Web Browser",
  "offers": {
    "@type": "Offer",
    "price": "0",
    "priceCurrency": "USD"
  },
  "provider": {
    "@type": "Organization",
    "name": "Mirats Insights Private Limited",
    "url": "https://www.miratsinsights.com"
  },
  "featureList": [
    "Survey Integration",
    "Target Audience Selection",
    "Custom Quota Management",
    "Qualtrics Integration",
    "Survey Monkey Integration",
    "Research Study Management"
  ]
};
